export const NAV = [
  { id: "about", label: "About" },
  { id: "research", label: "Research" },
  { id: "projects", label: "Projects & Affiliations" },
  { id: "cv", label: "CV" },
];

export const socialLinks = [
  { label: "ORCID", href: "https://orcid.org/0009-0002-4374-300X" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/kennedy-adriko-345170124" },
  { label: "GitHub", href: "https://github.com/adken" },
  { label: "Email", href: "mailto:k.adriko@fz-juelich.de" },
];

export const selectedResearch = [
  {
    eyebrow: "Published · IEEE JSTARS",
    title: "Crop-yield modelling across satellite resolutions",
    desc: "A matched spatiotemporal comparison of MODIS and Sentinel-2 for regional crop-yield prediction, designed to isolate the effect of sensor resolution and characteristics.",
    tags: ["Sentinel-2", "MODIS", "Crop yield"],
    links: [
      { label: "Paper", href: "https://doi.org/10.1109/JSTARS.2025.3624046" },
    ],
  },
  {
    eyebrow: "Ongoing research · GeoFMs",
    title: "Earth observation foundation models for agriculture",
    desc: "Benchmarking PRESTO, Clay, Prithvi-EO-2.0, TerraMind, and AlphaEarth for county-level corn-yield estimation, including multimodal fusion and spatial transfer evaluation.",
    tags: ["GeoFMs", "Benchmarking", "Spatial transfer"],
    links: [{ label: "Research", href: "#research" }],
  },
  {
    eyebrow: "Published · IEEE GRSM",
    title: "Neural compression for geospatial analytics",
    desc: "Research on efficient representations for large-scale Earth observation, including a review of lossy neural compression methods and their implications for geospatial analytics.",
    tags: ["Neural compression", "EO", "Scalability"],
    links: [
      { label: "Paper", href: "https://doi.org/10.1109/MGRS.2025.3546527" },
    ],
  },
];

export const researchInterests = [
  { icon: "🗜", title: "AI Compression", desc: "Efficient and scalable lossy neural compression of geospatial data for storage, transmission, and analytics at planetary scale." },
  { icon: "🛰", title: "Geospatial Foundation Models", desc: "Benchmarking and evaluation of large pretrained EO models (GeoFMs) for downstream agricultural and environmental tasks." },
  { icon: "⚡", title: "HPC for EO", desc: "Scalable geospatial ML pipelines on high-performance computing infrastructure for processing multi-modal and multi-temporal EO archives." },
];

export const affiliations = [
  "SDL AI and ML for Remote Sensing · JSC · Helmholtz",
  "Háskóli Íslands · University of Iceland",
  "FAST-EO · ESA Phi-Lab",
  "Embed2Scale · Horizon Europe",
  "Erasmus Mundus · Copernicus Master in Digital Earth",
];

export const publications = [
  {
    venue: "IEEE JSTARS · 2025",
    status: "Published",
    draft: false,
    title: "From MODIS to Sentinel-2: A Regional Comparative Analysis of Crop-Yield Prediction With Matched Spatiotemporal Data",
    authors: "Kennedy Adriko et al. · Forschungszentrum Jülich / JSC",
    summary: "Matched spatiotemporal evaluation of MODIS and Sentinel-2 for regional crop-yield prediction, establishing the empirical foundation for subsequent GeoFM benchmarking work.",
    tags: ["MODIS", "Sentinel-2", "Crop yield", "Time series", "Remote sensing"],
    links: [
      { label: "Paper", href: "https://doi.org/10.1109/JSTARS.2025.3624046" },
    ],
  },
  {
    venue: "IEEE GRSM · 2025",
    status: "Published · Co-author",
    draft: false,
    title: "Lossy Neural Compression for Geospatial Analytics: A Review",
    authors: "et al. including Kennedy Adriko · Forschungszentrum Jülich / JSC",
    summary: "A review of lossy neural compression techniques for geospatial and Earth observation data, covering methods, benchmarks, and implications for large-scale analytics pipelines.",
    tags: ["Neural compression", "Geospatial analytics", "EO data", "Review", "Embed2Scale"],
    links: [
      { label: "Paper", href: "https://doi.org/10.1109/MGRS.2025.3546527" },
    ],
  },
  {
    venue: "Ongoing research",
    status: "In preparation",
    draft: true,
    title: "From Pretraining to Prediction: Benchmarking Earth Observation Foundation Models for Corn Yield Estimation",
    authors: "Kennedy Adriko et al. · Forschungszentrum Jülich / JSC",
    summary: "A systematic benchmark of PRESTO, Clay 1.5, Prithvi-EO-2.0, TerraMind, and AlphaEarth for county-level corn-yield prediction, including multimodal fusion and spatial cross-validation.",
    tags: ["GeoFMs", "TerraMind", "PRESTO", "Clay", "AlphaEarth", "Benchmark", "Corn yield"],
    links: [],
  },
  {
    venue: "Master's thesis · 2023",
    status: null,
    draft: false,
    title: "Multi-modal Fusion of Optical and Radar Satellite Time Series for Deforestation Mapping",
    authors: "Kennedy Adriko · University of Salzburg / Université Bretagne Sud",
    summary: "Self-supervised learning with VICReg and dual TempCNN encoders for multimodal fusion of Sentinel-1 SAR and Sentinel-2 optical time series for Amazon deforestation mapping.",
    tags: ["VICReg", "SSL", "TempCNN", "Deforestation", "Amazon", "Sentinel-1"],
    links: [{ label: "Code", href: "https://github.com/adken/ssl-deforestation" }],
  },
];

export const projects = [
  { icon: "📡", name: "FAST-EO · TerraMind", desc: "ESA Phi-Lab funded project developing geospatial foundation models for Earth observation.", link: "https://www.fast-eo.eu/", linkLabel: "fast-eo.eu ↗" },
  { icon: "🌐", name: "Embed2Scale", desc: "EU Horizon Europe project using AI embeddings for EO data federation and neural compression.", link: "https://embed2scale.eu/", linkLabel: "embed2scale.eu ↗" },
  { icon: "🏔", name: "SDL AI and ML for Remote Sensing · JSC", desc: "The Simulation and Data Lab at Jülich Supercomputing Centre conducts interdisciplinary research at the intersection of remote sensing, large-scale AI, supercomputing, and quantum computing.", link: "https://www.fz-juelich.de/en/jsc/about-us/structure/simulation-and-data-labs/sdl-ai-ml-remote-sensing", linkLabel: "fz-juelich.de ↗" },
  { icon: "🧊", name: "SimDataLab RS · IHPC Iceland", desc: "The Icelandic HPC community's Simulation and Data Lab for Remote Sensing, jointly operated with JSC and connected to the PhD programme at the University of Iceland.", link: "https://www.ihpc.is/labs/simulation-and-data-lab-remote-sensing", linkLabel: "ihpc.is ↗" },
];

export const appointments = [
  { year: "2023 – present", title: "Doctoral Researcher", sub: "SDL AI and ML for Remote Sensing · JSC · Forschungszentrum Jülich · Helmholtz", detail: "Research on efficient and scalable AI compression, data fusion, and geospatial machine learning with HPC." },
  { year: "2023 – present", title: "FAST-EO Project", sub: "ESA Phi-Lab · Geospatial foundation model research (TerraMind)" },
  { year: "2023 – present", title: "Embed2Scale Project", sub: "EU Horizon Europe · AI embeddings and neural compression for EO data federation" },
];

export const education = [
  { year: "2024 – 2027", title: "PhD, Electrical and Computer Engineering", sub: "Háskóli Íslands · University of Iceland, Reykjavík", detail: "Degree-conferring institution. Research conducted at Forschungszentrum Jülich / JSC within the joint SDL for Remote Sensing." },
  { year: "2021 – 2023", title: "MSc Copernicus Master in Digital Earth", sub: "University of Salzburg & Université Bretagne Sud · Erasmus Mundus", detail: "Specialisation in Geodata Science. Thesis on multimodal self-supervised learning for deforestation mapping." },
  { year: "2012 – 2017", title: "BSc Land Surveying and Geomatics", sub: "Makerere University, Kampala, Uganda" },
];

export const cvPublications = publications.filter((publication) => publication.status?.startsWith("Published"));

export const technicalSkills = [
  "Python", "PyTorch", "Google Earth Engine", "Sentinel-1 / SAR", "Sentinel-2", "JUWELS / HPC",
  "scikit-learn", "NumPy", "Self-supervised learning", "Foundation models", "Neural compression",
  "Data fusion", "Time series analysis", "LaTeX", "Git",
];
